/**
 * @author: subhasish.ghosh@zoom.us
 * @author: ritesh.singh@zoom.us
 */

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Button } from "@zoom/react-ui";
import classNames from "classnames";
import dayjs from "dayjs";
import cloneDeep from "lodash/cloneDeep";
import map from "lodash/map";
import { columnsApprovalRejectionModal } from "@options/dataGrid";
import { MAX_APPROVER_COMMENT_LENGTH } from "@utils/constants";
import React, { useEffect, useMemo, useState } from "react";
import { DATE_FORMAT } from "@utils/timeUnits";
import Draggable from "./Draggable";
import {
  getCancellationReasonsForSelectedActionType,
  getFormattedAmount,
  hasMissingResolutionDate,
  isApproverLevelOne,
  isSelectedActionTypeBizOperationDirect,
  isSelectedActionTypeNonPaymentDirect,
  isValidDate,
} from "@utils/utils";
import "./styles.css";

const ApprovalRejectionModal = ({
  isOpen,
  onClose,
  selectedActionType,
  isApproveAction,
  approverLevel,
  selectedRows,
  setSelectedRowIds,
  handleApproveOrReject,
  isExecutionDecline,
  handleDeclineExecution,
}) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [comments, setComments] = useState("");
  const [promisedPayDate, setPromisedPayDate] = useState();
  const [isValidPromisedPayDate, setValidPromisedPayDate] = useState(false);
  const [promisedPayDateApplyRule, setPromisedPayDateApplyRule] = useState();
  const [cancellationReason, setCancellationReason] = useState("");
  const [
    selectedRecordsHasMissingPromisedPayDate,
    setSelectedRecordsHasMissingPromisedPayDate,
  ] = useState(false);

  const handleExtendedDateChange = (rowId, dayjsValue) => {
    const next = dayjsValue && dayjsValue.isValid()
        ? dayjsValue.format(DATE_FORMAT)
        : undefined;
    setData((old) =>
      (old || []).map((r) => (r.id === rowId ? { ...r, extendedExecutionDate: next } : r))
    );
  };

  const execExtensionValid = useMemo(() => {
    if (!isExecutionDecline || data.length !== 1) return false;
    const row = data[0];
    if (!row?.executionDate || !row?.extendedExecutionDate) return false;
    const exec = dayjs(row.executionDate);
    const ext = dayjs(row.extendedExecutionDate);
    if (!exec.isValid() || !ext.isValid()) return false;
    const afterExec = ext.isAfter(exec); // must be > execution date
    const within60 =
      ext.isSame(exec.add(60, "day")) || ext.isBefore(exec.add(60, "day"));
    return afterExec && within60;
  }, [isExecutionDecline, data]);

  // When Due for Cancel is 0, disable extension flow (input + button)
  const isDueZeroForExec = useMemo(() => {
    if (!isExecutionDecline || data.length !== 1) return false;
    const row = data[0];
    return Number(row?.dueIn) === 0;
  }, [isExecutionDecline, data]);

  const isDeferBlockedByExtension = useMemo(() => {
    if (!isExecutionDecline || data.length !== 1) return false;
    const row = data[0];
    return !!row?.cancellationExtensionDate;
  }, [isExecutionDecline, data]);

  // Single-row helper for defer flow
  const deferRow = useMemo(
    () => (isExecutionDecline && data.length === 1 ? data[0] : null),
    [isExecutionDecline, data]
  );

  // Info for banner: formatted executionDate and cancellationExtensionDate (Defer flow)
  const extensionBannerInfo = useMemo(() => {
    if (!isDeferBlockedByExtension || !deferRow) return null;

    const rawExec = deferRow.executionDate;
    const rawExt = deferRow.cancellationExtensionDate;

    const executionDate =
      rawExec && dayjs(rawExec).isValid()
        ? dayjs(rawExec).format(DATE_FORMAT)
        : rawExec;

    const cancellationExtensionDate =
      rawExt && dayjs(rawExt).isValid()
        ? dayjs(rawExt).format(DATE_FORMAT)
        : rawExt;

    return { executionDate, cancellationExtensionDate };
  }, [isDeferBlockedByExtension, deferRow]);

  // APPROVE FLOW — rows that block approval because extension sync is in progress
  const approvalBlockedRows = useMemo(
    () =>
      isApproveAction && !isExecutionDecline
        ? (data || []).filter((row) => !!row?.cancellationExtensionDate)
        : [],
    [isApproveAction, isExecutionDecline, data]
  );

  const approvalBlockedAccountNumbers = useMemo(
    () =>
      approvalBlockedRows
        .map((row) => row?.accountNumber || row?.id)
        .filter(Boolean),
    [approvalBlockedRows]
  );

  const isSingleBlocked =
    approvalBlockedRows.length === 1 && (data?.length || 0) === 1;

  const isApprovalBlockedByExtension = approvalBlockedRows.length > 0;

  const gridHeight = isExecutionDecline
    ? isDeferBlockedByExtension
      ? 148
      : 148
    : 400;

  const isDeclineAction = !isApproveAction;
  const isNonPaymentDirect =
    isSelectedActionTypeNonPaymentDirect(selectedActionType);
  const isBizOperationDirect =
    isSelectedActionTypeBizOperationDirect(selectedActionType);

  const disableApprovalRejectionBtn = useMemo(() => {
    // Defer flow: Block completely when extension is still syncing
    if (isExecutionDecline && isDeferBlockedByExtension) {
      return true;
    }

    // Defer flow: When due for cancel is 0, do not allow action
    if (isExecutionDecline && isDueZeroForExec) {
      return true;
    }

    // Approve flow – block when any selected record has cancellationExtensionDate
    if (isApproveAction && isApprovalBlockedByExtension) {
      return true;
    }

    // if no selected rows
    if (!data.length) return true;

    // Defer Cancellation Extension path: require valid extended date + comments
    if (isExecutionDecline) {
      const hasComments = !!(comments && comments.trim());
      return !execExtensionValid || !hasComments;
    }

    // if Decline action on Collections failure direct or Billing ops cancel direct
    if (isDeclineAction && (isNonPaymentDirect || isBizOperationDirect)) {
      // if resolution date is invalid or cancellation reason is not selected
      if (
        selectedRecordsHasMissingPromisedPayDate ||
        !cancellationReason?.length
      ) {
        return true;
      }
    }

    return false;
  }, [
    isApproveAction,
    cancellationReason,
    data,
    selectedActionType,
    selectedRecordsHasMissingPromisedPayDate,
    isExecutionDecline,
    execExtensionValid,
    isDueZeroForExec,
    comments,
    isDeferBlockedByExtension,
    isApprovalBlockedByExtension,
    isDeclineAction,
    isNonPaymentDirect,
    isBizOperationDirect,
  ]);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    setValidPromisedPayDate(isValidDate(promisedPayDate));
  }, [promisedPayDate]);

  useEffect(() => {
    selectedRows?.forEach((item) => {
      item.accountBalance = getFormattedAmount(item.accountBalance);
      item.subCmrr = getFormattedAmount(item.subCmrr);
    });
    setData(selectedRows || []);
  }, [selectedRows]);

  useEffect(() => {
    if (isSelectedActionTypeNonPaymentDirect(selectedActionType)) {
      if (promisedPayDate && promisedPayDateApplyRule) {
        const copyOfSelectedRow = cloneDeep(selectedRows);

        if (promisedPayDateApplyRule === "apply_to_all") {
          copyOfSelectedRow.map((item) => {
            item.promisedPayDate = promisedPayDate;
            return item;
          });
        } else if (promisedPayDateApplyRule === "apply_to_missing") {
          copyOfSelectedRow.map((item) => {
            if (!item.promisedPayDate) {
              item.promisedPayDate = promisedPayDate;
            }
            return item;
          });
        }

        setData(copyOfSelectedRow);
      }
    }
  }, [
    promisedPayDate,
    promisedPayDateApplyRule,
    selectedActionType,
    selectedRows,
  ]);

  useEffect(() => {
    // only in case of declining records and Collections Failure (Direct) action type
    if (isNonPaymentDirect && isDeclineAction) {
      setSelectedRecordsHasMissingPromisedPayDate(
        data?.some((item) => hasMissingResolutionDate(item.promisedPayDate)) ||
          false
      );
    }
  }, [isNonPaymentDirect, isDeclineAction, data]);

  const cleanUp = () => {
    setOpen(false);
    setData([]);
    setComments("");
    setPromisedPayDate();
    setValidPromisedPayDate(false);
    setPromisedPayDateApplyRule();
    setCancellationReason("");
    setSelectedRowIds([]);
  };

  const handleClose = () => {
    cleanUp();
    onClose(false);
  };

  const handleApproveOrRejectAction = (e) => {
    if (isExecutionDecline) {
      const row = data?.[0];
      handleDeclineExecution?.(row?.id, row?.extendedExecutionDate, comments);
      handleClose();
      return;
    }
    const isReject = e.target.textContent === "Decline Cancellation(s)";
    handleApproveOrReject(
      isReject ? "Rejected" : "Approved",
      comments,
      cancellationReason,
      promisedPayDate,
      promisedPayDateApplyRule
    );
    handleClose();
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        className="cancellation-approval-rejection-modal"
      >
        <Box
          className="cancellation-approval-rejection-box"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <DialogTitle id="modal-title" style={{ padding: 0 }}>
            <Box display="flex" justifyContent="space-between">
              <Typography
                id="modal-title"
                variant="h6"
                component="h2"
                style={{ fontWeight: "bold" }}
              >
                {isApproveAction
                  ? "Approve Cancellation"
                  : isExecutionDecline
                  ? "Defer Cancellation"
                  : "Decline Cancellation"}
              </Typography>
              <Box>
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>

          {/* Defer flow banner when there is an existing cancellationExtensionDate */}
          {extensionBannerInfo && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                mt: 2,
                mb: 1.5,
                fontSize: "0.9rem",
                backgroundColor: "#fffbea",
                borderColor: "#ffe082",
                color: "#5d4037",
              }}
            >
              The latest cancellation execution date extension (current:{" "}
              <b>{extensionBannerInfo.executionDate}</b> → proposed:{" "}
              <b>{extensionBannerInfo.cancellationExtensionDate}</b>)  is 
              currently in progress for synchronization with Zuora. 
              Please wait until the sync completes, or contact Zuora IT 
              Support if it takes longer than 2 hours.
            </Alert>
          )}

          {/* Defer flow banner when Due for Cancel (dueIn) is 0 */}
          {isExecutionDecline && isDueZeroForExec && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                mt: extensionBannerInfo ? 0 : 2,
                mb: 1.5,
                fontSize: "0.9rem",
                backgroundColor: "#fffbea",
                borderColor: "#ffe082",
                color: "#5d4037",
              }}
            >
              The cancellation execution date cannot be extended as the subscription is scheduled to cancel today. Please contact the Billing team for assistance.
            </Alert>
          )}

          {/* Approve flow banner for records blocked by sync-in-progress extension */}
          {isApproveAction && isApprovalBlockedByExtension && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                mt: extensionBannerInfo || (isExecutionDecline && isDueZeroForExec) ? 0 : 2,
                mb: 1.5,
                fontSize: "0.9rem",
                backgroundColor: "#fffbea",
                borderColor: "#ffe082",
                color: "#5d4037",
              }}
            >
              {isSingleBlocked ? (
                <>
                  This record is currently in progress for synchronization with Zuora. 
                  Please wait until the sync completes, or contact Zuora IT Support if it takes longer than 2 hours
                </>
              ) : (
                <>
                  The following record(s) are currently in progress for
                  synchronization in Zuora and cannot be approved:{" "}
                  <b>{approvalBlockedAccountNumbers.join(", ")}</b>. Please wait
                  until the synchronization is completed, or remove these
                  record(s) from your selection to proceed with approving the
                  remaining records.
                </>
              )}
            </Alert>
          )}

          {!isValidPromisedPayDate &&
          isNonPaymentDirect &&
          isDeclineAction &&
          selectedRecordsHasMissingPromisedPayDate ? (
            <Alert
              variant="standard"
              severity="warning"
              style={{
                padding: "0 10px",
                fontSize: "0.8rem",
                textAlign: "left",
              }}
            >
              There are records without resolution date. Please select{" "}
              <b>Resolution Date</b> before proceeding.
            </Alert>
          ) : (
            <></>
          )}

          <Box sx={{ height: gridHeight, width: "100%", margin: "16px 0" }}>
            <DataGrid
              rows={data}
              columns={columnsApprovalRejectionModal(
                selectedActionType,
                isApproveAction,
                isExecutionDecline,
                handleExtendedDateChange,
                isDeferBlockedByExtension // disable Extend Upto input when syncing
              )}
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnSelector
              columnHeaderHeight={80}
              rowHeight={65}
              autoHeight={false}
              autoPageSize={false}
              hideFooter
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-scrollbar": {
                  position: "absolute",
                },
              }}
              getRowClassName={(params) => {
                const rowHasMissingPromisedDate = hasMissingResolutionDate(
                  params?.row?.promisedPayDate
                );
                const isPromisedMissingDateEligible =
                  isSelectedActionTypeNonPaymentDirect(selectedActionType) &&
                  isDeclineAction;
                return isPromisedMissingDateEligible &&
                  rowHasMissingPromisedDate
                  ? classNames(
                      `approve-reject-datagrid-row-${params?.id}`,
                      "missingmissing-promised-pay-date-row"
                    )
                  : `approve-reject-datagrid-row-${params?.id}`;
              }}
            />
          </Box>

          {/* Hide comments / extra controls when extension is still syncing (Defer flow)
              OR when Due for Cancel is 0 (Defer flow) */}
          {!(
            isExecutionDecline &&
            (isDeferBlockedByExtension || isDueZeroForExec)
          ) && (
            <CancellationReasonAndComments
              selectedActionType={selectedActionType}
              approverLevel={approverLevel}
              isDeclineAction={isDeclineAction}
              isNonPaymentDirect={isNonPaymentDirect}
              isBizOperationDirect={isBizOperationDirect}
              isValidPromisedPayDate={isValidPromisedPayDate}
              isExecutionDecline={isExecutionDecline}
              comments={comments}
              setComments={setComments}
              setCancellationReason={setCancellationReason}
              setPromisedPayDate={setPromisedPayDate}
              setPromisedPayDateApplyRule={setPromisedPayDateApplyRule}
            />
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            {!isExecutionDecline && (<Typography>{`${data?.length} results`}</Typography>)}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
              {isApproveAction ? (
                <Button
                  type="primary"
                  size="large"
                  className={classNames("apprv_rjct_btn", "popup")}
                  onClick={handleApproveOrRejectAction}
                  disabled={disableApprovalRejectionBtn}
                >
                  Approve Cancellation(s)
                </Button>
              ) : (
                // For Defer flow:
                //  - hide the Defer button when extension is syncing
                //  - hide the Defer button when Due for Cancel is 0
                (!isExecutionDecline ||
                  (!isDeferBlockedByExtension && !isDueZeroForExec)) && (
                  <Button
                    size="large"
                    variant="contained"
                    className={classNames("apprv_rjct_btn", "popup")}
                    danger
                    onClick={handleApproveOrRejectAction}
                    disabled={disableApprovalRejectionBtn}
                  >
                  {isExecutionDecline ? "Defer Cancellation" : "Decline Cancellation(s)"}
                  </Button>
                )
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

const CancellationReasonAndComments = ({
  selectedActionType,
  approverLevel,
  isDeclineAction,
  isNonPaymentDirect,
  isBizOperationDirect,
  isValidPromisedPayDate,
  isExecutionDecline,
  comments,
  setComments,
  setCancellationReason,
  setPromisedPayDate,
  setPromisedPayDateApplyRule,
}) => {
  const cancellationReasonsForSelectedActionType =
    getCancellationReasonsForSelectedActionType(selectedActionType);
  const [value, setValue] = useState("");
  const items = map(cancellationReasonsForSelectedActionType, (value, key) => {
    return { label: value, key: key };
  });

  const handleReasonChange = (event, selectedActionType) => {
    const newReason = event.target.value;
    setValue(newReason);
    setCancellationReason(cancellationReasonsForSelectedActionType[newReason]);
  };

  return (
    <Box sx={{ display: "grid" }}>
      <Box sx={{ display: "grid" }}>
        {isDeclineAction && (isNonPaymentDirect || isBizOperationDirect) ? (
          <FormControl
            sx={{ margin: "8px 0", padding: 1, width: 300, display: "flex" }}
            size="small"
          >
            <InputLabel
              id="select_label"
              shrink={true}
              className="select_label"
            >
              Reason For Declining
            </InputLabel>
            <Select
              labelId="select_label"
              id="select_label"
              value={value}
              label="cancellation_reasons&"
              onChange={(e) => handleReasonChange(e, selectedActionType)}
              renderValue={(value) =>
                value ? (
                  <label style={{ fontSize: "14px", color: "#323539" }}>
                    {cancellationReasonsForSelectedActionType[value]}
                  </label>
                ) : (
                  <></>
                )
              }
              displayEmpty
              required
            >
              {items.map(({ label, key }) => (
                <MenuItem key={key} value={key}>
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <></>
        )}
        {isDeclineAction && isNonPaymentDirect ? (
          <Box className="promised-pay-date-container">
            <DatePicker
              className={classNames("select_label", "promised-pay-date-picker")}
              label="Resolution Date"
              onChange={(updatedDate) => {
                setPromisedPayDate(updatedDate?.format(DATE_FORMAT));
                // for approver level 1, set apply all by default and do not show apply all/ apply missing radio
                if (isApproverLevelOne(approverLevel))
                  setPromisedPayDateApplyRule("apply_to_all");
              }}
              maxDate={dayjs().add(30, "day")}
              minDate={dayjs()}
              format={DATE_FORMAT}
              slotProps={{
                textField: {
                  variant: "outlined",
                  margin: "dense",
                  size: "small",
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                },
              }}
            />
            {/* for approver level 1, set apply all by default and do not show apply all/ apply missing radio */}
            {!isApproverLevelOne(approverLevel) ? (
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby="apply-promised-pay-date-group-label"
                  name="row-radio-buttons-group"
                  className="promised-pay-date-radio"
                  onChange={(e) => {
                    setPromisedPayDateApplyRule(e.target.value);
                  }}
                >
                  <FormControlLabel
                    value="apply_to_all"
                    control={<Radio />}
                    label="Apply To All"
                    disabled={!isValidPromisedPayDate}
                    aria-disabled={!isValidPromisedPayDate}
                  />
                  <FormControlLabel
                    value="apply_to_missing"
                    control={<Radio />}
                    label="Apply Only To Missing"
                    disabled={!isValidPromisedPayDate}
                    aria-disabled={!isValidPromisedPayDate}
                  />
                </RadioGroup>
              </FormControl>
            ) : (
              <></>
            )}
          </Box>
        ) : (
          <></>
        )}
      </Box>

      <TextField
        multiline
        rows={4}
        placeholder="Please provide any additional comments"
        variant="outlined"
        fullWidth
        sx={{ mt: 2, marginTop: 0 }}
        onChange={(e) =>
          setComments(e.target.value.slice(0, MAX_APPROVER_COMMENT_LENGTH))
        }
        value={comments || ""}
        required={isExecutionDecline}
        error={isExecutionDecline && !comments?.trim()}
        inputProps={{ maxLength: MAX_APPROVER_COMMENT_LENGTH }}
        FormHelperTextProps={{
          sx: { display: "flex", justifyContent: "space-between" },
        }}
        helperText={
          <>
            <span>
              {isExecutionDecline && !comments?.trim()
                ? "Comments are required to defer cancellation."
                : ""}
            </span>
            <span>{`${(comments || "").length}/${MAX_APPROVER_COMMENT_LENGTH}`}</span>
          </>
        }
      />
    </Box>
  );
};

export default ApprovalRejectionModal;
